"use client"

import { useEffect } from "react"

import type { AlgoliaProductHit, AlgoliaPromoCardHit } from "~/lib/algolia/types"
import { COLLECTION_HITS_PER_PAGE } from "~/lib/shopify/constants"
import { useEmitCustomAnalyticsEvent } from "~/hooks/tracking/useEmitCustomAnalyticsEvent"
import useLocale from "~/hooks/useLocale"
import { isProductCardHit } from "~/components/ui/AlgoliaCollectionGrid/AlgoliaListingGrid/_utils/check-hit-type"
import { serializeListItemTracking } from "~/components/ui/AlgoliaCollectionGrid/AlgoliaListingGrid/_utils/serialize-list-item-tracking"
import type { ViewedItemListPayload } from "~/components/shared/AnalyticsEvents/types"
import { useAlgoliaCustomContext } from "~/providers/AlgoliaCustomContext/index.client"
import { useCollectionTree } from "~/managers/CollectionsTreeManager/hooks"

type ClientViewItemListProps<T> = T & {
  collectionProps: {
    title: string
    handle: string
  }
}

export function AlgoliaClientViewItemList({
  results,
  collectionProps,
}: ClientViewItemListProps<{
  results: (AlgoliaProductHit | AlgoliaPromoCardHit)[]
}>) {
  const { emitCustomEvent } = useEmitCustomAnalyticsEvent()

  const [collectionTree] = useCollectionTree()
  const { page } = useAlgoliaCustomContext()
  const locale = useLocale()

  function getEventListPayload() {
    const currentPage = page ? page - 1 : 0

    let startIndex = currentPage * COLLECTION_HITS_PER_PAGE
    const endIndex = (page ?? 1) * COLLECTION_HITS_PER_PAGE

    if (currentPage > 0 && results.length < COLLECTION_HITS_PER_PAGE) {
      startIndex = 0
    }

    const resultsCopy = [...results]

    const paginatedHits = resultsCopy.slice(startIndex, endIndex)
    const paginatedProductHits = paginatedHits.filter(isProductCardHit)

    const itemsTracking =
      paginatedProductHits?.map((item, index) => {
        const serializedItemTracking = serializeListItemTracking({
          item,
          collectionTree,
          locale,
        })

        return {
          ...serializedItemTracking,
          index,
          item_list_id: collectionProps.handle,
          item_list_name: collectionProps.title,
        }
      }) ?? []

    return {
      item_list_id: collectionProps.handle || "",
      item_list_name: `${collectionProps.title} - Page ${page ?? 1}`,
      items: itemsTracking,
    } satisfies ViewedItemListPayload
  }

  useEffect(() => {
    const eventListPayload = getEventListPayload()

    emitCustomEvent<"view_item_list">("view_item_list", { ecommerce: eventListPayload })
  }, [results?.length])

  return null
}
